<template>
    <div class="details-stack">
        <a
            href="#"
            class="back-link"
            @click.prevent="goBack"
        >
            {{ $t('certificates.back_to_overview') }}
        </a>

        <DetailsCard>
            <DRow class="align-items-center">
                <DCol sm="12">
                    <a
                        :href="'https://' + certificate.domain"
                        target="_blank"
                        class="domain-link"
                    >
                        {{ certificate.domain }}
                    </a>
                    <CopyToClipboard
                        :value="certificate.domain"
                        class="ms-2"
                    />
                </DCol>
            </DRow>
            <div class="overview-chips">
                <DTag
                    v-if="certificate.sslType"
                    :label="certificate.sslType"
                    color="primary"
                />
                <DTag
                    v-if="certificate.cert_type"
                    :label="certificate.cert_type"
                    color="secondary"
                />
                <DTag
                    v-if="certificate.multi_san === true"
                    :label="$t('certificates.multi_san_badge', { count: activeSanCount })"
                    color="info"
                />
                <DTag
                    :label="certificate.autoRenew ? $t('certificates.auto_renew_yes') : $t('certificates.auto_renew_no')"
                    :color="certificate.autoRenew ? 'success' : 'secondary'"
                />
            </div>
            <DRow class="overview-meta">
                <DCol sm="4">
                    <span class="meta-label">{{ $t('certificates.table_expires_at') }}</span>
                    <span class="meta-value">{{ $formatDateTime(certificate.expiresAt) || '—' }}</span>
                </DCol>
                <DCol sm="4">
                    <span class="meta-label">{{ $t('certificates.table_created_at') }}</span>
                    <span class="meta-value">{{ $formatDateTime(certificate.created_at) || '—' }}</span>
                </DCol>
                <DCol sm="4">
                    <span class="meta-label">{{ $t('certificates.table_updated_at') }}</span>
                    <span class="meta-value">{{ $formatDateTime(certificate.updated_at) || '—' }}</span>
                </DCol>
            </DRow>
        </DetailsCard>

        <DRow class="gy-3">
            <DCol sm="6">
                <DetailsCard :title="$t('certificates.table_issuance')">
                    <!-- Awaiting-DCV badge + Check now action sit together
                         on a normal <DRow>. We deliberately do NOT use a
                         dynamic named slot (<template v-if #title-right>):
                         Vue 3 doesn't always re-notify the child component
                         when a dynamic named slot toggles after the first
                         render, so the button only appeared after a hard
                         page refresh once the external flag was flipped. -->
                    <DRow
                        v-if="isAwaitingExternalDcv"
                        class="status-row"
                    >
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.external_dcv_label') }}</span>
                            <div class="external-dcv-row">
                                <DTag
                                    :label="$t('certificates.awaiting_external_dcv')"
                                    color="info"
                                    :title="$t('certificates.awaiting_external_dcv_tooltip')"
                                />
                                <DButton
                                    v-if="canRecheck"
                                    icon="refresh"
                                    color="link"
                                    mini
                                    :title="$t('certificates.recheck_tooltip')"
                                    :disabled="recheckSubmitting"
                                    @click="triggerRecheck"
                                >
                                    {{ $t('certificates.recheck_button') }}
                                </DButton>
                            </div>
                        </DCol>
                    </DRow>
                    <DRow class="status-row">
                        <DCol sm="6">
                            <span class="meta-label">{{ $t('certificates.status_label') }}</span>
                            <DTag
                                :label="statusLabel(issuanceStatus)"
                                :color="statusColor(issuanceStatus)"
                            />
                        </DCol>
                        <DCol sm="6">
                            <span class="meta-label">{{ $t('certificates.job_id_label') }}</span>
                            <span class="meta-value mono">{{ certificate.certRundeckJobId || '—' }}</span>
                        </DCol>
                    </DRow>
                    <DRow
                        v-if="certificate.certRundeckJobErrors > 0 || certificate.certRundeckJobErrorMessage"
                        class="error-row"
                    >
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.error_count_label', { count: certificate.certRundeckJobErrors || 0 }) }}</span>
                            <span class="meta-value error-message">{{ certificateErrorMessage }}</span>
                            <span
                                v-if="issuanceRetryHint"
                                class="meta-value text-muted retry-hint"
                            >
                                {{ issuanceRetryHint }}
                            </span>
                        </DCol>
                    </DRow>
                    <DRow v-if="certificate.certRundeckJobNextExecution">
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.next_execution_label') }}</span>
                            <span class="meta-value">{{ $formatDateTime(certificate.certRundeckJobNextExecution) }}</span>
                        </DCol>
                    </DRow>
                    <DRow v-if="certificate.last_provider_poll_at">
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.last_provider_poll_label') }}</span>
                            <span class="meta-value">{{ $formatDateTime(certificate.last_provider_poll_at) }}</span>
                        </DCol>
                    </DRow>
                </DetailsCard>
            </DCol>
            <DCol sm="6">
                <DetailsCard :title="$t('certificates.table_installation')">
                    <DRow
                        v-if="hasExternalSan"
                        class="status-row"
                    >
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.table_installation') }}</span>
                            <DTag
                                :label="$t('certificates.managed_externally_label')"
                                color="info"
                                :title="$t('certificates.managed_externally_tooltip')"
                            />
                        </DCol>
                    </DRow>
                    <DRow class="status-row">
                        <DCol sm="6">
                            <span class="meta-label">{{ $t('certificates.status_label') }}</span>
                            <DTag
                                :label="statusLabel(installStatus)"
                                :color="statusColor(installStatus)"
                            />
                        </DCol>
                        <DCol sm="6">
                            <span class="meta-label">{{ $t('certificates.job_id_label') }}</span>
                            <span class="meta-value mono">{{ certificate.installRundeckJobId || '—' }}</span>
                        </DCol>
                    </DRow>
                    <DRow
                        v-if="hostInstallations.length > 0"
                        class="status-row"
                    >
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.hosting_platforms_label') }}</span>
                            <DTag
                                v-for="installation in hostInstallations"
                                :key="installation.id"
                                :label="`${installation.hostname ?? '—'}: ${statusLabel(hostInstallationStatus(installation))}`"
                                :color="statusColor(hostInstallationStatus(installation))"
                                :title="hostInstallationTooltip(installation)"
                            />
                        </DCol>
                    </DRow>
                    <DRow
                        v-if="certificate.installRundeckJobErrors > 0 || certificate.installRundeckJobErrorMessage"
                        class="error-row"
                    >
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.error_count_label', { count: certificate.installRundeckJobErrors || 0 }) }}</span>
                            <span class="meta-value error-message">{{ certificate.installRundeckJobErrorMessage || '—' }}</span>
                            <span
                                v-if="installRetryHint"
                                class="meta-value text-muted retry-hint"
                            >
                                {{ installRetryHint }}
                            </span>
                        </DCol>
                    </DRow>
                    <DRow v-if="certificate.installRundeckJobNextExecution">
                        <DCol sm="12">
                            <span class="meta-label">{{ $t('certificates.next_execution_label') }}</span>
                            <span class="meta-value">{{ $formatDateTime(certificate.installRundeckJobNextExecution) }}</span>
                        </DCol>
                    </DRow>
                </DetailsCard>
            </DCol>
        </DRow>

        <DetailsCard
            v-if="isIssued"
            :title="$t('certificates.downloads_title')"
        >
            <div class="download-actions">
                <DButton
                    :href="$route('certificates.download-file', { certificate: certificate.id, type: 'cert' })"
                    target="_self"
                    icon="download"
                    :label="$t('certificates.download_cert')"
                    mini
                />
                <DButton
                    v-if="certificate.caCert"
                    :href="$route('certificates.download-file', { certificate: certificate.id, type: 'chain' })"
                    target="_self"
                    icon="download"
                    :label="$t('certificates.download_chain')"
                    mini
                />
                <DButton
                    v-if="certificate.caCert"
                    :href="$route('certificates.download-file', { certificate: certificate.id, type: 'fullchain' })"
                    target="_self"
                    icon="download"
                    :label="$t('certificates.download_fullchain')"
                    mini
                />
                <DButton
                    v-if="certificate.privateKey"
                    :href="$route('certificates.download-file', { certificate: certificate.id, type: 'key' })"
                    target="_self"
                    icon="download"
                    :label="$t('certificates.download_key')"
                    mini
                />
            </div>
        </DetailsCard>

        <DRow class="gy-3">
            <DCol sm="6">
                <DetailsCard
                    :title="$t('certificates.domains_title', { count: displayDomains.length })"
                >
                    <template
                        v-if="canMutateSans"
                        #title-right
                    >
                        <DButton
                            icon="pencil"
                            color="link"
                            mini
                            :title="$t('certificate_domains.manage_sans_tooltip')"
                            class="san-edit-trigger"
                            @click="openSanMutation"
                        />
                    </template>
                    <div
                        v-for="group in domainGroups"
                        :key="group.method"
                        class="san-group"
                    >
                        <p class="san-group-label">
                            {{ group.label }}
                            <span class="san-group-count">{{ group.rows.length }}</span>
                        </p>
                        <ul class="san-list">
                            <li
                                v-for="row in group.rows"
                                :key="row.id || row.domain"
                            >
                                <div class="san-headline">
                                    <span class="san-domain">
                                        {{ row.domain }}
                                        <CopyToClipboard
                                            :value="row.domain"
                                            class="ms-2"
                                        />
                                    </span>
                                    <div class="san-actions">
                                        <DTag
                                            v-if="isPrimary(row)"
                                            :label="$t('certificate_domains.primary_tag')"
                                            color="primary"
                                        />
                                        <DTag
                                            v-if="row.external"
                                            :label="$t('certificate_domains.external_tag')"
                                            color="info"
                                        />
                                        <DTag
                                            :label="dcvStatusLabel(row.dcv_status)"
                                            :color="dcvStatusColor(row.dcv_status)"
                                        />
                                        <DButtonDropdown
                                            v-if="rowHasMenu(row)"
                                            mini
                                            color="secondary"
                                        >
                                            <DDropdownItem
                                                v-if="canOverride(row)"
                                                icon="pencil"
                                                @click="openOverrideModal(row)"
                                            >
                                                {{ $t('dcv_method_override.action_change') }}
                                            </DDropdownItem>
                                            <DDropdownItem
                                                v-if="canToggleExternal(row)"
                                                :icon="row.external ? 'lan-connect' : 'web'"
                                                @click="toggleExternal(row)"
                                            >
                                                {{ row.external ? $t('certificate_domains.mark_internal') : $t('certificate_domains.mark_external') }}
                                            </DDropdownItem>
                                        </DButtonDropdown>
                                    </div>
                                </div>
                                <div
                                    v-if="row.dcv_method === 'cname' && cnameFor(row)"
                                    class="san-cname"
                                >
                                    <div class="cname-row">
                                        <span class="cname-label">{{ $t('certificate_domains.cname_host_label') }}</span>
                                        <span class="cname-value mono">{{ cnameFor(row).name }}</span>
                                        <CopyToClipboard :value="cnameFor(row).name" />
                                    </div>
                                    <div class="cname-row">
                                        <span class="cname-label">{{ $t('certificate_domains.cname_target_label') }}</span>
                                        <span class="cname-value mono">{{ cnameFor(row).value }}</span>
                                        <CopyToClipboard :value="cnameFor(row).value" />
                                    </div>
                                </div>
                                <div
                                    v-if="row.dcv_method === 'email' && row.dcv_email_address"
                                    class="san-cname"
                                >
                                    <div class="cname-row">
                                        <span class="cname-label">{{ $t('certificate_domains.dcv_email_label') }}</span>
                                        <span class="cname-value mono">{{ row.dcv_email_address }}</span>
                                        <CopyToClipboard :value="row.dcv_email_address" />
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </DetailsCard>
            </DCol>
            <DCol sm="6">
                <DetailsCard :title="$t('certificates.subject_title')">
                    <p
                        v-if="usesDefaultSubject"
                        class="text-muted mb-0"
                    >
                        {{ $t('certificates.default_subject_notice') }}
                    </p>
                    <DRow
                        v-for="field in subjectFields"
                        v-else
                        :key="field.key"
                        class="subject-row"
                    >
                        <DCol sm="5">
                            <span class="meta-label">{{ field.label }}</span>
                        </DCol>
                        <DCol sm="7">
                            <span class="meta-value">{{ field.value }}</span>
                        </DCol>
                    </DRow>
                </DetailsCard>
            </DCol>
        </DRow>

        <DetailsCard
            v-if="certificate.sectigo_order_id"
            :title="$t('certificates.external_references_title')"
        >
            <DRow>
                <DCol sm="6">
                    <span class="meta-label">{{ $t('certificates.sectigo_order_id_label') }}</span>
                    <span class="meta-value mono">
                        {{ certificate.sectigo_order_id }}
                        <CopyToClipboard :value="certificate.sectigo_order_id" />
                    </span>
                </DCol>
                <DCol sm="6">
                    <span class="meta-label">{{ $t('certificates.sectigo_order_status_label') }}</span>
                    <a
                        href="https://secure.trust-provider.com/products/ORDERSTATUSCHECKER"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="meta-value sectigo-status-link"
                    >
                        {{ $t('certificates.sectigo_status_link') }}
                    </a>
                </DCol>
            </DRow>
        </DetailsCard>

        <DetailsCard :title="$t('certificates.audit_log_title')">
            <p
                v-if="events.length === 0"
                class="text-muted mb-0"
            >
                {{ $t('certificates.audit_log_empty') }}
            </p>
            <ul
                v-else
                class="event-list"
            >
                <li
                    v-for="event in events"
                    :key="event.id"
                    class="event-row"
                >
                    <div class="event-headline">
                        <span class="event-time">{{ $formatDateTime(event.created_at) || '—' }}</span>
                        <span class="event-actor">
                            {{ event.actor ? (event.actor.name || event.actor.email) : $t('certificates.audit_log_system_actor') }}
                        </span>
                        <span
                            v-if="event.certificate_domain"
                            class="event-domain mono"
                        >
                            {{ event.certificate_domain.domain }}
                        </span>
                    </div>
                    <div class="event-body">
                        {{ labelFor(event) }}
                    </div>
                </li>
            </ul>
        </DetailsCard>

        <DFabs>
            <DFab
                icon="redo"
                :label="$t('certificates.action_restart')"
                @click="restart"
            />
            <DFab
                icon="undo"
                :label="$t('certificates.action_reinstall')"
                @click="reinstall"
            />
        </DFabs>

        <DModal
            v-if="overrideTarget"
            :title="$t('dcv_method_override.modal_title', { domain: overrideTarget.domain })"
            :show="overrideTarget !== null"
            @close="closeOverrideModal"
        >
            <DCard padding>
                <DFormRow
                    :label="$t('dcv_method_override.new_method_label')"
                    :error="overrideErrors.dcv_method"
                    is-required
                >
                    <DRadioGroup
                        id="override-dcv-method"
                        v-model="overrideForm.dcv_method"
                        inline
                        :options="[
                            { value: 'cname', label: $t('dcv_method_override.method_cname') },
                            { value: 'email', label: $t('dcv_method_override.method_email') }
                        ]"
                    />
                </DFormRow>
                <DFormRow
                    v-if="overrideForm.dcv_method === 'email'"
                    :label="$t('dcv_method_override.recipient_address_label')"
                    :error="overrideErrors.email_address"
                    is-required
                    :hint="$t('dcv_method_override.recipient_address_hint')"
                >
                    <p
                        v-if="emailAddressesLoading"
                        class="text-muted mb-0"
                    >
                        {{ $t('dcv_method_override.loading_addresses') }}
                    </p>
                    <template v-else>
                        <p
                            v-if="emailAddressesError"
                            class="text-danger mb-2"
                        >
                            {{ emailAddressesError }}
                        </p>
                        <DSelect
                            v-model="emailAddressChoice"
                            :options="emailAddressOptions"
                        />
                        <DInput
                            v-if="emailAddressChoice === CUSTOM_EMAIL_SENTINEL"
                            v-model="overrideForm.email_address"
                            :placeholder="$t('dcv_method_override.email_placeholder')"
                            type="email"
                            class="mt-2"
                        />
                    </template>
                </DFormRow>
                <DFormRow
                    :label="$t('dcv_method_override.reason_label')"
                    :error="overrideErrors.reason"
                    is-required
                >
                    <DInput
                        v-model="overrideForm.reason"
                        :placeholder="$t('dcv_method_override.reason_placeholder')"
                    />
                </DFormRow>
            </DCard>
            <template #buttons>
                <DButton
                    :label="$t('dcv_method_override.apply_button')"
                    color="primary"
                    :disabled="overrideForm.processing"
                    @click="submitOverride"
                />
            </template>
        </DModal>

        <SanMutationModal
            :certificate="certificate"
            :show-modal="sanMutationOpen"
            :can-add="canAddSans"
            :can-remove="canRemoveSans"
            :can-set-primary="canSetPrimary"
            @close-san-mutation-modal="closeSanMutation"
        />

        <DModal
            :title="$t('certificates.recheck_modal_title')"
            :show="recheckConfirmOpen"
            is-confirm
            @close="recheckConfirmOpen = false"
            @confirm="confirmRecheck"
        >
            <div
                v-if="recheckConfirmOpen"
                class="m-3"
            >
                <p class="mb-2">
                    {{ $t('certificates.recheck_confirm_prefix') }}
                    <strong>{{ certificate.domain }}</strong>.
                </p>
                <p class="text-muted small mb-0">
                    {{ $t('certificates.recheck_confirm_hint') }}
                </p>
            </div>
        </DModal>
    </div>
</template>

<script>
import { router, useForm, usePage } from '@inertiajs/vue3'
import CopyToClipboard from '../../Components/Common/CopyToClipboard.vue'
import DetailsCard from '../../Components/Certificates/DetailsCard.vue'
import SanMutationModal from '../../Components/Certificates/Modals/SanMutation.vue'
import { auditEventLabel } from '../../Components/Certificates/auditEventLabel.js'

const PERMISSION_DCV_METHOD_OVERRIDE = 'certificate.dcv_method_override'
const PERMISSION_ADD_DOMAINS = 'certificate.add-domains'
const PERMISSION_REMOVE_DOMAINS = 'certificate.remove-domains'
const PERMISSION_SET_PRIMARY = 'certificate.set-primary'
const PERMISSION_EDIT_SANS = 'certificate.edit-sans'
const PERMISSION_RECHECK = 'certificate.recheck'
const CUSTOM_EMAIL_SENTINEL = '__custom__'

const STATUS_COLORS = {
    succeeded: 'success',
    running: 'warning',
    pending: 'secondary',
    retry: 'warning',
    failed: 'danger'
}

const STATUS_LABEL_KEYS = {
    succeeded: 'certificates.status_succeeded',
    running: 'certificates.status_running',
    pending: 'certificates.status_pending',
    retry: 'certificates.status_retry',
    failed: 'certificates.status_failed'
}

const DCV_STATUS_COLORS = {
    pending: 'secondary',
    validating: 'warning',
    email_sent: 'info',
    validated: 'success',
    failed: 'danger'
}

const DCV_STATUS_LABEL_KEYS = {
    pending: 'certificate_domains.dcv_status_pending',
    validating: 'certificate_domains.dcv_status_validating',
    email_sent: 'certificate_domains.dcv_status_email_sent',
    validated: 'certificate_domains.dcv_status_validated',
    failed: 'certificate_domains.dcv_status_failed',
    unknown: 'certificate_domains.dcv_status_unknown'
}

const SUBJECT_FIELD_DEFS = [
    { key: 'organization', labelKey: 'certificates.subject_organization' },
    { key: 'department', labelKey: 'certificates.subject_department' },
    { key: 'country', labelKey: 'certificates.subject_country' },
    { key: 'state', labelKey: 'certificates.subject_state' },
    { key: 'location', labelKey: 'certificates.subject_location' },
    { key: 'email', labelKey: 'certificates.subject_email' },
    { key: 'phone', labelKey: 'certificates.subject_phone' },
    { key: 'address', labelKey: 'certificates.subject_address' },
    { key: 'postal_code', labelKey: 'certificates.subject_postal_code' }
]

export default {
    name: 'CertificateShow',
    components: { CopyToClipboard, DetailsCard, SanMutationModal },
    props: {
        certificate: {
            type: Object,
            required: true
        },
        events: {
            type: Array,
            default: () => []
        },
        dnsValidationRecords: {
            type: Object,
            default: () => ({})
        },
        hostInstallations: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            overrideTarget: null,
            overrideForm: useForm({
                dcv_method: 'cname',
                email_address: '',
                reason: ''
            }),
            emailAddresses: [],
            emailAddressesLoading: false,
            emailAddressesError: '',
            emailAddressChoice: '',
            sanMutationOpen: false,
            recheckConfirmOpen: false,
            recheckSubmitting: false,
            CUSTOM_EMAIL_SENTINEL
        }
    },
    computed: {
        hasMultipleDomains () {
            return this.activeSanCount > 1
        },
        activeSanCount () {
            const rows = Array.isArray(this.certificate?.domains) ? this.certificate.domains : []
            return rows.filter(row => row.active !== false).length
        },
        displayDomains () {
            const rows = Array.isArray(this.certificate?.domains) ? this.certificate.domains : []

            // Only show currently-active SANs. Inactive rows are kept in the
            // database for audit (CIO-11426) but they're not part of the cert
            // any more, so they don't belong on the Domains card.
            const active = rows.filter(row => row.active !== false)

            if (active.length > 0) {
                return active
            }

            // Legacy single-domain certs have no `certificate_domains` rows;
            // surface the primary domain with an unknown dcv_status so the
            // card layout is consistent across every cert.
            return [{ domain: this.certificate.domain, dcv_status: null }]
        },
        /**
         * Bucket the active SANs by DCV method so CS can see at a
         * glance which set is verified by CNAME and which by email,
         * then sort each bucket alphabetically by domain.
         */
        domainGroups () {
            const groupOrder = [
                { method: 'cname', label: this.$t('certificate_domains.group_cname') },
                { method: 'email', label: this.$t('certificate_domains.group_email') },
                { method: 'unknown', label: this.$t('certificate_domains.group_unknown') }
            ]

            const buckets = {}
            for (const row of this.displayDomains) {
                const key = row.dcv_method === 'email' || row.dcv_method === 'cname'
                    ? row.dcv_method
                    : 'unknown'

                if (!buckets[key]) {
                    buckets[key] = []
                }
                buckets[key].push(row)
            }

            return groupOrder
                .filter(group => Array.isArray(buckets[group.method]) && buckets[group.method].length > 0)
                .map(group => ({
                    ...group,
                    rows: [...buckets[group.method]].sort((a, b) => a.domain.localeCompare(b.domain))
                }))
        },
        issuanceStatus () {
            return this.deriveStatus(
                this.certificate.certRundeckJobStatus,
                this.certificate.certRundeckJobNextExecution
            )
        },
        installStatus () {
            return this.deriveStatus(
                this.certificate.installRundeckJobStatus,
                this.certificate.installRundeckJobNextExecution
            )
        },
        certificateErrorMessage () {
            const key = this.certificate.cert_rundeck_job_error_key

            return key
                ? this.$t(`certificate_errors.${key}`, this.certificate.cert_rundeck_job_error_context || {})
                : (this.certificate.certRundeckJobErrorMessage || '—')
        },
        issuanceRetryHint () {
            return this.retryHint(
                this.certificate.certRundeckJobStatus,
                this.certificate.certRundeckJobNextExecution,
                this.certificate.certRundeckJobErrorMessage
            )
        },
        installRetryHint () {
            return this.retryHint(
                this.certificate.installRundeckJobStatus,
                this.certificate.installRundeckJobNextExecution,
                this.certificate.installRundeckJobErrorMessage
            )
        },
        usesDefaultSubject () {
            return !this.certificate.organization
        },
        subjectFields () {
            return SUBJECT_FIELD_DEFS
                .map(def => ({ key: def.key, label: this.$t(def.labelKey), value: this.certificate[def.key] }))
                .filter(f => f.value !== null && f.value !== undefined && f.value !== '')
        },
        canOverrideDcvMethod () {
            const permissions = usePage().props?.auth?.permissions || []
            return permissions.includes(PERMISSION_DCV_METHOD_OVERRIDE)
        },
        canAddSans () {
            return (usePage().props?.auth?.permissions || []).includes(PERMISSION_ADD_DOMAINS)
        },
        canRemoveSans () {
            return (usePage().props?.auth?.permissions || []).includes(PERMISSION_REMOVE_DOMAINS)
        },
        canMutateSans () {
            // SAN edits only make sense for multi-SAN certs. Single-domain
            // certs have a CN equal to the single domain by construction
            // and can't carry additional SANs.
            return this.certificate?.multi_san === true && (this.canAddSans || this.canRemoveSans)
        },
        canSetPrimary () {
            return this.certificate?.multi_san === true &&
                (usePage().props?.auth?.permissions || []).includes(PERMISSION_SET_PRIMARY)
        },
        canEditSans () {
            return (usePage().props?.auth?.permissions || []).includes(PERMISSION_EDIT_SANS)
        },
        hasExternalSan () {
            const rows = Array.isArray(this.certificate?.domains) ? this.certificate.domains : []
            return rows.some(row => row.active !== false && row.external === true)
        },
        isIssued () {
            return this.certificate?.certRundeckJobStatus === 'succeeded'
        },
        isAwaitingExternalDcv () {
            // CIO-11633: shown when at least one SAN is external and the cert
            // has not yet been issued. Communicates to CS that the cert is
            // legitimately waiting on customer-side DCV publication.
            return this.hasExternalSan && !this.isIssued
        },
        canRecheck () {
            return (usePage().props?.auth?.permissions || []).includes(PERMISSION_RECHECK)
        },
        emailAddressOptions () {
            return [
                ...this.emailAddresses.map(address => ({ value: address, label: address })),
                { value: CUSTOM_EMAIL_SENTINEL, label: this.$t('dcv_method_override.custom_address_option') }
            ]
        },
        overrideErrors () {
            return this.overrideForm.errors || {}
        }
    },
    watch: {
        emailAddressChoice (value) {
            if (value === CUSTOM_EMAIL_SENTINEL) {
                this.overrideForm.email_address = ''
                return
            }
            this.overrideForm.email_address = value || ''
        }
    },
    methods: {
        deriveStatus (status, nextExecution) {
            if (status === null || status === undefined) {
                return nextExecution ? 'retry' : 'pending'
            }

            return status
        },
        hostInstallationStatus (installation) {
            return this.deriveStatus(installation.job_status, installation.job_next_execution)
        },
        hostInstallationTooltip (installation) {
            if (!installation.job_error_message) {
                return null
            }

            return [
                `${this.$t('certificates.error_count_label', { count: installation.job_errors })}: ${installation.job_error_message}`,
                this.retryHint(installation.job_status, installation.job_next_execution, installation.job_error_message)
            ].join('\n')
        },
        statusLabel (status) {
            return this.$t(STATUS_LABEL_KEYS[status] ?? status)
        },
        dcvStatusLabel (status) {
            return this.$t(DCV_STATUS_LABEL_KEYS[status ?? 'unknown'] ?? (status ?? 'unknown'))
        },
        // "failed" is terminal; any other status after an error is still
        // eligible for the next automatic retry.
        retryHint (status, nextExecution, errorMessage) {
            if (!errorMessage) {
                return null
            }

            if (status === 'failed') {
                return this.$t('certificates.retry_hint_terminal')
            }

            return nextExecution
                ? this.$t('certificates.retry_hint_scheduled', { time: this.$formatDateTime(nextExecution) })
                : this.$t('certificates.retry_hint_pending')
        },
        statusColor (label) {
            return STATUS_COLORS[label] ?? 'secondary'
        },
        labelFor (event) {
            return auditEventLabel(event.event_type, event.payload)
        },
        restart () {
            router.patch(this.$route('certificates.restart', { certificates: this.certificate.id }))
        },
        reinstall () {
            router.patch(this.$route('certificates.reinstall', { certificates: this.certificate.id }))
        },
        canOverride (row) {
            return this.canOverrideDcvMethod && row && row.dcv_status === 'pending' && row.id
        },
        dcvStatusColor (status) {
            return DCV_STATUS_COLORS[status] ?? 'secondary'
        },
        isPrimary (row) {
            return row && row.domain === this.certificate?.domain
        },
        rowHasMenu (row) {
            return this.canOverride(row) || this.canToggleExternal(row)
        },
        canToggleExternal (row) {
            // Needs the row's DB id to target the per-SAN endpoint; legacy
            // single-domain certs without certificate_domains rows can't be
            // toggled.
            return this.canEditSans && !!(row && row.id)
        },
        toggleExternal (row) {
            if (!row || !row.id) {
                return
            }

            router.put(
                this.$route('certificates.domains.set-external', {
                    certificate: this.certificate.id,
                    domain: row.id
                }),
                { external: !row.external },
                { preserveScroll: true }
            )
        },
        triggerRecheck () {
            // CIO-11633: re-check is recorded in the audit log and costs a
            // provider call, so confirm with the agent before submitting.
            this.recheckConfirmOpen = true
        },
        confirmRecheck () {
            this.recheckConfirmOpen = false
            this.recheckSubmitting = true
            router.post(
                this.$route('certificates.recheck', { certificate: this.certificate.id }),
                {},
                {
                    preserveScroll: true,
                    onFinish: () => {
                        this.recheckSubmitting = false
                    }
                }
            )
        },
        cnameFor (row) {
            return this.dnsValidationRecords[row.domain] || null
        },
        openOverrideModal (row) {
            this.overrideTarget = row
            this.overrideForm.clearErrors()
            this.overrideForm.dcv_method = row.dcv_method === 'email' ? 'email' : 'cname'
            this.overrideForm.email_address = row.dcv_email_address || ''
            this.overrideForm.reason = ''
            this.emailAddressChoice = ''
            this.loadEmailAddresses(row)
        },
        closeOverrideModal () {
            this.overrideTarget = null
            this.overrideForm.clearErrors()
            this.emailAddresses = []
            this.emailAddressesLoading = false
            this.emailAddressesError = ''
            this.emailAddressChoice = ''
        },
        loadEmailAddresses (row) {
            this.emailAddresses = []
            this.emailAddressesError = ''
            this.emailAddressesLoading = true

            window.axios
                .get(this.$route('certificates.domains.dcv-email-addresses', {
                    certificate: this.certificate.id,
                    domain: row.id
                }))
                .then(response => {
                    this.emailAddresses = Array.isArray(response.data?.data) ? response.data.data : []
                    if (response.data?.error) {
                        this.emailAddressesError = this.$t('dcv_method_override.load_addresses_error')
                    }
                    this.applyInitialEmailChoice()
                })
                .catch(() => {
                    this.emailAddresses = []
                    this.emailAddressesError = this.$t('dcv_method_override.load_addresses_error')
                    this.applyInitialEmailChoice()
                })
                .finally(() => {
                    this.emailAddressesLoading = false
                })
        },
        applyInitialEmailChoice () {
            const current = this.overrideForm.email_address || ''
            if (current && this.emailAddresses.includes(current)) {
                this.emailAddressChoice = current
                return
            }

            if (current && !this.emailAddresses.includes(current)) {
                // Pre-filled value the provider doesn't list — treat as a
                // custom entry so the agent can keep it or pick another.
                this.emailAddressChoice = CUSTOM_EMAIL_SENTINEL
                return
            }

            this.emailAddressChoice = this.emailAddresses.length > 0
                ? this.emailAddresses[0]
                : CUSTOM_EMAIL_SENTINEL
        },
        submitOverride () {
            if (!this.overrideTarget) {
                return
            }

            this.overrideForm.post(
                this.$route('certificates.domains.override-dcv-method', {
                    certificate: this.certificate.id,
                    domain: this.overrideTarget.id
                }),
                {
                    onSuccess: () => this.closeOverrideModal()
                }
            )
        },
        openSanMutation () {
            this.sanMutationOpen = true
        },
        closeSanMutation () {
            this.sanMutationOpen = false
        },
        goBack () {
            const referrer = document.referrer
            const sameOrigin = referrer && new URL(referrer).origin === window.location.origin

            if (sameOrigin && window.history.length > 1) {
                window.history.back()
                return
            }

            router.visit(this.$route('certificates.index'))
        }
    }
}
</script>

<style scoped>
.details-stack > * + * {
    margin-top: 1rem;
}
.details-stack :deep(.d-card) {
    height: 100%;
}
.back-link {
    display: inline-block;
    font-size: 0.875rem;
    color: var(--bs-link-color, #0d6efd);
    text-decoration: none;
}
.back-link:hover {
    text-decoration: underline;
}
.domain-link {
    font-size: 1.25rem;
    font-weight: 600;
    word-break: break-all;
}
.overview-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.85rem;
}
.overview-meta {
    margin-top: 1rem;
}
.meta-label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color, #6c757d);
    margin-bottom: 0.2rem;
}
.meta-value {
    display: block;
    word-break: break-word;
}
.meta-value.mono,
.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    font-size: 0.875rem;
}
.status-row > * {
    margin-bottom: 0.5rem;
}
.external-dcv-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}
.error-row {
    margin-top: 0.5rem;
}
.error-message {
    color: var(--bs-danger, #dc3545);
}
.retry-hint {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}
.subject-row + .subject-row {
    margin-top: 0.4rem;
}
.san-group + .san-group {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--bs-border-color, #dee2e6);
}
.san-group-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.25rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color, #6c757d);
}
.san-group-count {
    padding: 0 0.4rem;
    border-radius: 10px;
    background: var(--bs-secondary-bg-subtle, #e9ecef);
    color: var(--bs-secondary-color, #6c757d);
    font-size: 0.7rem;
    font-weight: 600;
}
.san-list,
.event-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.san-list li {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.5rem 0;
}
.san-list li + li {
    border-top: 1px solid var(--bs-border-color, #dee2e6);
}
.san-headline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
}
.san-domain {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    font-size: 0.875rem;
}
.san-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    justify-content: flex-end;
}
.download-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.san-cname {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.4rem 0.6rem;
    background-color: var(--bs-tertiary-bg, #f8f9fa);
    border-radius: 4px;
    font-size: 0.8rem;
}
.cname-row {
    display: grid;
    grid-template-columns: 5.5rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
}
.cname-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color, #6c757d);
}
.cname-value {
    min-width: 0;
    word-break: break-all;
}
.sectigo-status-link {
    display: inline-block;
    color: var(--bs-link-color, #0d6efd);
    text-decoration: underline;
    cursor: pointer;
}
.sectigo-status-link:hover {
    text-decoration: none;
}
.event-row {
    padding: 0.6rem 0;
}
.event-row + .event-row {
    border-top: 1px solid var(--bs-border-color, #dee2e6);
}
.event-headline {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 0.75rem;
    color: var(--bs-secondary-color, #6c757d);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.event-actor {
    font-weight: 600;
}
.event-domain {
    text-transform: none;
    letter-spacing: 0;
}
.event-body {
    margin-top: 0.2rem;
    font-size: 0.95rem;
    word-break: break-word;
}
</style>
